import type { Directive } from 'vue';

const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789áéíóúãõçÁÉÍÓÚÃÕÇ';

/**
 * v-cipher — the "decryption" effect on language switch, with size
 * reservation for the longest variant.
 *
 * Usage on LEAF text elements only (no child nodes, no {{ }} interpolation):
 *   <span v-cipher="$t('hero.titleB')"></span>
 *
 * Size rule:
 *  - During the animation the scramble never exceeds the longest of the
 *    two variants (old vs new language) — the layout doesn't jump.
 *  - After each settle the element RESERVES the largest width AND height
 *    it has ever had (min-width/min-height) — so switching languages never
 *    changes a component's size: it always stays at the longest text.
 *
 * Short and smooth: ~180ms for short texts, ~400ms max for long ones.
 * Respects prefers-reduced-motion.
 */
export const cipher: Directive<HTMLElement, string> = {
  mounted(el, binding) {
    el.textContent = binding.value ?? '';
    el.dataset.cipherPrev = binding.value ?? '';
  },
  updated(el, binding) {
    const next = binding.value;
    const prev = el.dataset.cipherPrev ?? '';
    if (next == null || prev === next) return;

    const settle = () => {
      el.textContent = next;
      el.dataset.cipherPrev = next;
      reserveSize(el);
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      settle();
      return;
    }

    // The cipher is always the size of the longest variant — never bigger,
    // never smaller mid-flight.
    const maxLen = Math.max(prev.length, next.length, Number(el.dataset.cipherMax ?? 0));
    const frames = Math.max(4, Math.min(9, Math.round(maxLen / 6)));
    const frameMs = 45;
    let frame = 0;

    const timer = setInterval(() => {
      frame++;
      const progress = frame / frames;
      // Slight ease-out + tight left-to-right solve for a smooth feel
      const eased = 1 - Math.pow(1 - progress, 1.15);
      let out = '';
      for (let i = 0; i < maxLen; i++) {
        // Preserve whitespace from BOTH variants: if the scramble dropped
        // the spaces, long paragraphs would lose their line breaks and turn
        // into a single flat overflowing line.
        const isSpace =
          (i < next.length && next[i] === ' ') ||
          (i < prev.length && prev[i] === ' ');
        if (isSpace) {
          out += ' ';
          continue;
        }
        const revealAt = (i / maxLen) * 1.08 - 0.04 + Math.random() * 0.04;
        out += eased >= revealAt && i < next.length
          ? next[i]
          : CHARS[Math.floor(Math.random() * CHARS.length)];
      }
      el.textContent = out;
      if (frame >= frames) {
        clearInterval(timer);
        el.dataset.cipherMax = String(maxLen);
        settle();
      }
    }, frameMs);
  },
};

/**
 * After a settle, remembers the largest rendered width/height ever seen on
 * this element and pins it with min-width/min-height. Combined with the
 * fixed per-component min-heights, no component ever changes size when the
 * language switches — it stays at the longest text's size.
 */
function reserveSize(el: HTMLElement): void {
  const rect = el.getBoundingClientRect();
  const curW = Math.ceil(rect.width);
  const curH = Math.ceil(rect.height);

  const maxW = Math.max(Number(el.dataset.cipherW ?? 0), curW);
  const maxH = Math.max(Number(el.dataset.cipherH ?? 0), curH);

  el.dataset.cipherW = String(maxW);
  el.dataset.cipherH = String(maxH);

  if (maxW > 0) el.style.minWidth = `${maxW}px`;
  if (maxH > 0) el.style.minHeight = `${maxH}px`;

  // min-width only applies to non-inline elements; keep spans reserving space
  if (getComputedStyle(el).display === 'inline') {
    el.style.display = 'inline-block';
  }
}